import * as THREE from 'three';
import { FragmentManager } from './fragments.js';
import { Player } from './player.js';
import { makeRng } from '../engine/rng.js';
import { getCharacterById } from './characters.js';

export function setAssets(game, patch = {}) {
  if ('playerFbx' in patch) game._playerFbx = patch.playerFbx || null;
  if ('boss01Fbx' in patch) game._boss01Fbx = patch.boss01Fbx || null;
  if ('boss02Fbx' in patch) game._boss02Fbx = patch.boss02Fbx || null;
  const kitbashFbx = 'kitbashFbx' in patch ? patch.kitbashFbx : undefined;
  // Extract a few prop meshes from kitbash
  if (kitbashFbx) {
    const props = [];
    kitbashFbx.traverse((c) => {
      if (c.isMesh && c.geometry && (!c.parent || c.parent.type !== 'Bone')) {
        // Clone each mesh as a self-contained prop
        const m = c.clone();
        // Reset transform so anchor placement is predictable
        m.position.set(0, 0, 0);
        m.rotation.set(0, 0, 0);
        m.scale.set(1, 1, 1);
        // Ensure sensible size: aim for ~1.2m height
        const box = new THREE.Box3().setFromObject(m);
        const size = new THREE.Vector3();
        box.getSize(size);
        if (size.y > 0.01) {
          const s = 1.2 / size.y;
          m.scale.setScalar(s);
        }
        // Recentre on floor
        const box2 = new THREE.Box3().setFromObject(m);
        m.position.y -= box2.min.y;
        m.castShadow = true;
        m.receiveShadow = true;
        props.push(m);
      }
    });
    // Cap at 10 distinct props
    game.kitbashProps = props.slice(0, 10);
    game._kitbashLoaded = game.kitbashProps.length > 0;
  }
}

export function setAssetOrchestrator(game, orchestrator) {
  game._assetOrchestrator = orchestrator || null;
}

export function triggerBossPreload(game, phase) {
  if (!game._assetOrchestrator) return;
  if (game._bossPreloadFired[phase]) return;
  game._bossPreloadFired[phase] = true;
  const p = phase === 2
    ? game._assetOrchestrator.ensureBoss02?.()
    : game._assetOrchestrator.ensureBoss01?.();
  Promise.resolve(p).catch(() => {});
}

export function startRun(game, seed, totalRooms) {
  game.runMode = 'run';
  game.totalRooms = totalRooms;
  startSession(game, seed);
}

export function startSandbox(game, seed, sandboxTotalRooms) {
  game.runMode = 'sandbox';
  game.totalRooms = sandboxTotalRooms;
  startSession(game, seed);
  game._setSandboxDebugPanelVisible(true);
  game.toast('SANDBOX READY - PRESS 1-7 TO SPAWN');
}

export function startSession(game, seed) {
  game._sessionSeed = Number(seed) >>> 0;
  game._lastRoomSeed = 0;
  game.rng = makeRng(seed);
  game.fragments = new FragmentManager(game.rng);
  game.roomIndex = 0;
  game.state = 'playing';
  game.spawnRuntime?.stop();
  game._sandboxGodMode = false;
  game._clearEnemiesAndProjectiles();
  game._clearShards();
  game._clearAmmoPickups();
  game._clearWeaponPickups();
  game._clearGroundWeaponPickups();
  game._clearLootPickups();
  game._clearPlayerMines();
  if (game.room) { game.room.dispose(); game.room = null; }
  // Reset boss preload guards per run so re-starting fires ensure() again
  // if the previous attempt failed.
  game._bossPreloadFired = { 1: false, 2: false };

  if (!game.player) {
    game.player = new Player({ scene: game.scene, fbx: game._playerFbx, audio: game.audio });
  }

  game._buildCurrentRoom();
  game.transitionPostprocessToRoom?.(game.roomIndex + 1);
  const profile = getCharacterById(game._selectedCharacterId);
  game.player.applyCharacterProfile?.(profile);
  const spawnX = game.room?.playerSpawn?.x || 0;
  const spawnZ = game.room?.playerSpawn?.z || 0;
  game.player.reset(spawnX, spawnZ);
  game.hud.show();
  game.hud.setFragments([]);
  game._setSandboxDebugPanelVisible(game.runMode === 'sandbox');
  game._applySandboxInfiniteAmmo();
  game.audio.playMusic('combat', { fadeInMs: 0, fadeOutPreviousMs: 0 });
}

export function applySandboxInfiniteAmmo(game) {
  if (!game.player) return;
  const on = game.runMode === 'sandbox' && (game._sandboxInfiniteAmmoInput?.checked ?? false);
  game.player.setInfiniteAmmo(on);
}

export function isPlayerHiddenFromEnemies(game) {
  return game.runMode === 'sandbox' && game._sandboxGodMode;
}

export function setSandboxGodMode(game, on) {
  game._sandboxGodMode = !!on;
  if (!game.player) return;
  game.player.setDebugHidden(game._sandboxGodMode);
  game.player.invuln = game._sandboxGodMode ? Number.POSITIVE_INFINITY : 0;
  game.toast(game._sandboxGodMode ? 'GOD MODE ON - INVISIBLE TO ENEMIES' : 'GOD MODE OFF');
}

export function clearEnemiesAndProjectiles(game) {
  game._outlineTargetEnemy = null;
  for (const e of game.enemies) e.dispose();
  game.enemies.length = 0;
  game._pendingBurstShots.length = 0;
  game.projectiles.forEach((p) => { p.mesh.visible = false; p.alive = false; });
  game.projectiles.clear();
  game.slashFx.clear();
}


